/**
 * WebCrypto primitives used by the browser client.
 *
 * STRICT algorithm choices (per spec):
 *   - Key agreement:  X25519 (ECDH)
 *   - Symmetric:      AES-256-GCM
 *   - KDF:            HKDF-SHA256
 *   - Hash:           SHA-256
 *
 * Private keys are generated NON-EXTRACTABLE so raw private material never
 * leaves the WebCrypto boundary (limits blast radius if the page is compromised).
 */

const subtle = crypto.subtle;

/**
 * Cast a byte view to BufferSource. Runtime-safe (WebCrypto accepts any
 * TypedArray); works around TS 5.7 making Uint8Array generic over its buffer,
 * which otherwise rejects ArrayBufferLike-backed arrays at the DOM boundary.
 */
const bs = (u: Uint8Array): BufferSource => u as unknown as BufferSource;

export function randomBytes(n: number): Uint8Array {
  const b = new Uint8Array(n);
  crypto.getRandomValues(b);
  return b;
}

export async function sha256(data: Uint8Array): Promise<Uint8Array> {
  return new Uint8Array(await subtle.digest("SHA-256", bs(data)));
}

export async function hmacSha256(
  keyBytes: Uint8Array,
  data: Uint8Array
): Promise<Uint8Array> {
  const key = await subtle.importKey(
    "raw",
    bs(keyBytes),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"]
  );
  return new Uint8Array(await subtle.sign("HMAC", key, bs(data)));
}

export async function hkdfSha256(
  ikm: Uint8Array,
  salt: Uint8Array,
  info: Uint8Array,
  length = 32
): Promise<Uint8Array> {
  const key = await subtle.importKey("raw", bs(ikm), "HKDF", false, [
    "deriveBits",
  ]);
  const bits = await subtle.deriveBits(
    { name: "HKDF", hash: "SHA-256", salt: bs(salt), info: bs(info) },
    key,
    length * 8
  );
  return new Uint8Array(bits);
}

/* --------------------------------- X25519 -------------------------------- */

export function x25519Supported(): boolean {
  // Feature-detected lazily; generateKey will still throw a clear error if not.
  return typeof subtle?.generateKey === "function";
}

/** Generate an X25519 key pair with a NON-EXTRACTABLE private key. */
export async function generateX25519(): Promise<CryptoKeyPair> {
  return (await subtle.generateKey({ name: "X25519" }, false, [
    "deriveBits",
  ])) as CryptoKeyPair;
}

export async function exportRawPublic(key: CryptoKey): Promise<Uint8Array> {
  return new Uint8Array(await subtle.exportKey("raw", key));
}

export async function importRawX25519Public(
  raw: Uint8Array
): Promise<CryptoKey> {
  return subtle.importKey("raw", bs(raw), { name: "X25519" }, true, []);
}

/** Raw 32-byte X25519 shared secret between our private key and a peer public. */
export async function x25519Shared(
  privateKey: CryptoKey,
  peerPublic: CryptoKey
): Promise<Uint8Array> {
  const bits = await subtle.deriveBits(
    { name: "X25519", public: peerPublic },
    privateKey,
    256
  );
  return new Uint8Array(bits);
}

/* ------------------------------- AES-256-GCM ------------------------------ */

export async function importAesGcmKey(raw: Uint8Array): Promise<CryptoKey> {
  if (raw.length !== 32) throw new Error("AES-256 key must be 32 bytes");
  return subtle.importKey("raw", bs(raw), { name: "AES-GCM" }, false, [
    "encrypt",
    "decrypt",
  ]);
}

export async function aesGcmEncrypt(
  key: CryptoKey,
  iv: Uint8Array,
  plaintext: Uint8Array,
  aad?: Uint8Array
): Promise<Uint8Array> {
  const params: AesGcmParams = { name: "AES-GCM", iv: bs(iv) };
  if (aad) params.additionalData = bs(aad);
  return new Uint8Array(await subtle.encrypt(params, key, bs(plaintext)));
}

export async function aesGcmDecrypt(
  key: CryptoKey,
  iv: Uint8Array,
  ciphertext: Uint8Array,
  aad?: Uint8Array
): Promise<Uint8Array> {
  const params: AesGcmParams = { name: "AES-GCM", iv: bs(iv) };
  if (aad) params.additionalData = bs(aad);
  return new Uint8Array(await subtle.decrypt(params, key, bs(ciphertext)));
}

/** Length-independent constant-time comparison. */
export function constantTimeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a[i]! ^ b[i]!;
  return diff === 0;
}
